<script setup lang="ts">
import { isTouchScreen } from '@@/bits/browser'
import { getWhiteboardContentSources } from '@@/bits/content_picker'
import { dir } from '@@/bits/current_dir'
import device from '@@/bits/device'
import { isAppUsing } from '@@/bits/flip'
import { tailwindFlattenedColors } from '@@/bits/tailwind_config'
import { getTailwindColorClass } from '@@/bits/whiteboard'
import { isPadletTool, toStoredToolId } from '@@/bits/whiteboard_tools'
import CursorDottedShapeIcon from '@@/library/v4/components/CursorDottedShapeIcon.vue'
import OzBaseButton from '@@/library/v4/components/OzBaseButton.vue'
import OzBasePopover, { OzBasePopoverColors } from '@@/library/v4/components/OzBasePopover.vue'
import OzIcon from '@@/library/v4/components/OzIcon.vue'
import { useSurfacePermissionsStore } from '@@/pinia/surface_permissions'
import { useWhiteboardStore } from '@@/pinia/whiteboard'
import { useWhiteboardToolbarStore } from '@@/pinia/whiteboard_toolbar'
import { useWhiteboardToolsStore } from '@@/pinia/whiteboard_tools'
import type { WhiteboardSecondaryMenu, WhiteboardTool } from '@@/types/whiteboard'
import WhiteboardSvgIcon, { SVG_ICONS } from '@@/vuecomponents/WhiteboardSvgIcon.vue'
import { offset } from '@floating-ui/vue'
import { Portal } from '@linusborg/vue-simple-portal'
import { ContentPickerSource } from '@padlet/universal-post-editor'
import { computed, ref } from 'vue'

const whiteboardToolbarStore = useWhiteboardToolbarStore()
const whiteboardToolsStore = useWhiteboardToolsStore()
const whiteboardStore = useWhiteboardStore()
const surfacePermissionsStore = useSurfacePermissionsStore()

const props = withDefaults(
  defineProps<{
    toolId: string
    isExpandedSelectTool?: boolean
    colorScheme?: 'dark' | 'light'
    colorStyle?: 'frosted' | 'opaque'
    iconName: string
    iconColor?: string | null
    currentToolColor?: string | null
    isSelected?: boolean
    label: string
    shortcut?: string
    xTooltip?: boolean
    secondaryMenu?: WhiteboardSecondaryMenu
    secondarySelectionId?: string
    testId?: string
    disabled?: boolean
  }>(),
  {
    isExpandedSelectTool: false,
    colorScheme: 'dark',
    colorStyle: 'frosted',
    iconName: 'cursor',
    iconColor: null,
    currentToolColor: null,
    isSelected: false,
    label: '',
    shortcut: '',
    xTooltip: false,
    secondaryMenu: undefined,
    secondarySelectionId: undefined,
    testId: undefined,
    disabled: false,
  },
)
const emit = defineEmits(['hoveron', 'hoveroff', 'click'])

const isHovered = ref(false)
const isActive = ref(false)
function onPointerEnter(): void {
  isHovered.value = true
  emit('hoveron')
}
function onPointerLeave(): void {
  isHovered.value = false
  emit('hoveroff')
}

function onPointerDown(event: PointerEvent | MouseEvent): void {
  isActive.value = true
  emit('click', event)
  // To prevent the button from staying focused after the click
  // So that when user use a keyboard shortcut to trigger another button, this button will become :focus-visible
  unfocusButton()
}
function onPointerUp(): void {
  isActive.value = false
}
function unfocusButton(): void {
  if (buttonEl.value) {
    buttonEl.value.blur()
  }
}

const isToolAPadletTool = computed(() => isPadletTool(props.toolId))

const buttonRef = ref<typeof OzBaseButton | null>(null)
const buttonEl = computed(() => buttonRef.value?.$el)

const isSvgIcon = computed(() => iconName.value != null && SVG_ICONS.includes(iconName.value))
const iconColor = computed((): string | null => {
  if (props.iconColor) {
    return props.iconColor
  }
  // If there is a secondaryMenu and secondaryMenuSelectionId, get the color from the secondaryMenu
  if (props.secondaryMenu && props.secondarySelectionId) {
    const secondaryMenuSelection = props.secondaryMenu.items.find(
      (menuItem) => menuItem.id === props.secondarySelectionId,
    )
    if (secondaryMenuSelection) {
      return secondaryMenuSelection.id
    }
  }
  return null
})

const iconColorClass = computed(() => (iconColor.value != null ? getTailwindColorClass(iconColor.value) : null))
const currentToolPreviewColor = computed(() => {
  const isDarkMode = props.colorScheme === 'dark'
  const isLightMode = props.colorScheme === 'light'
  const isDarkText100 = props.currentToolColor === 'dark-text-100'
  const isLightText100 = props.currentToolColor === 'light-text-100'

  if (!isDarkText100 && !isLightText100) {
    return props.currentToolColor ?? 'transparent'
  }
  if (isDarkMode && isDarkText100) {
    if (isHovered.value && !props.isSelected) {
      return 'grey-600'
    }
    if (props.isSelected) {
      return 'grey-700'
    }
  }

  if (isLightMode && isLightText100) {
    if (isHovered.value && !props.isSelected) {
      return 'grey-200'
    }
    if (props.isSelected) {
      return 'grey-100'
    }
  }
  return props.currentToolColor ?? 'transparent'
})
const currentToolPreviewColorHex = computed(() => {
  return tailwindFlattenedColors[currentToolPreviewColor.value]
})
const cornerIndicatorNeedsDiagonalLine = computed(() => {
  if (
    props.currentToolColor === 'dark-text-100' &&
    ((props.colorScheme === 'dark' && !props.isSelected) || (props.colorScheme === 'light' && props.isSelected))
  ) {
    return true
  }

  if (
    props.currentToolColor === 'light-text-100' &&
    ((props.colorScheme === 'light' && !props.isSelected) || (props.colorScheme === 'dark' && props.isSelected))
  ) {
    return true
  }
  return false
})
/**
 * For geo tool, we render the selected geo tool (e.g. rectangle, oval, since there is no generic geo icon
 * For color, we would use the iconColor passed in from props. (@see WhiteboardToolbar.vue )
 */
const getSecondaryMenuProperty = (property: 'iconName' | 'label' | 'shortcut'): string | undefined => {
  if (props.iconName === 'geo') {
    return property === 'iconName' ? 'geo_edit_style' : props[property]
  }

  if (props.iconName === 'draw-nested' && props.secondaryMenu && props.secondarySelectionId) {
    const secondaryMenuSelection = props.secondaryMenu.items.find(
      (menuItem) => menuItem.id === props.secondarySelectionId,
    )
    return secondaryMenuSelection ? secondaryMenuSelection[property] : props[property]
  }
  return props[property]
}

const iconName = computed(() => getSecondaryMenuProperty('iconName'))
const label = computed(() => getSecondaryMenuProperty('label'))
const shortcut = computed(() => getSecondaryMenuProperty('shortcut'))

// Web doesn't support MediaLibrary content source so we filter it out.
const contentSources = getWhiteboardContentSources().filter((s) => {
  return !device.app ? s.type !== ContentPickerSource.MediaLibrary : true
})

const enabledContentSources = computed(() => {
  return contentSources.filter((s) => whiteboardToolsStore.allEnabledToolIds.includes(toStoredToolId(s.type)))
})

const contentSourcesDisplayedInToolbar = computed(() => {
  return (
    whiteboardToolbarStore.displayedToolSections
      .flat(999) // TypeScript can't infer the type correctly if we pass Infinity
      // as the depth argument, so we pass an arbitrary large number instead.
      // `displayedToolSections` is only a 3D array so this should flatten it
      // completely.
      .filter((tool: WhiteboardTool) => isPadletTool(tool.id))
  )
})

const numberOfAttachmentTypesAllowed = computed(() => {
  // This doesn't affect admins since they can see and use all tools.
  if (surfacePermissionsStore.canIAdminister) return contentSources.length
  const numEnabledPadletTools = enabledContentSources.value.length
  const numPadletToolsInToolbar = contentSourcesDisplayedInToolbar.value.length
  return numEnabledPadletTools - numPadletToolsInToolbar
})

const isTouch = isTouchScreen()
const isRtl = dir() === 'rtl'
</script>

<template>
  <div class="relative flex justify-center">
    <!-- Button -->
    <OzBaseButton
      ref="buttonRef"
      :data-testid="testId"
      :size-preset="undefined"
      :color-scheme="undefined"
      :dark-mode="colorScheme === 'dark'"
      :aria-label="label"
      :class="[
        'group',

        // Shape
        {
          'h-11 w-11':
            (isAppUsing('sandboxCardNavigation') && whiteboardStore.isLoadingPortraitMobileLayout) ||
            (isTouch && !isExpandedSelectTool),
          'h-10 w-10': !isTouch && !isExpandedSelectTool,
          'h-10 w-20': isExpandedSelectTool,
        },
        'rounded-lg',
        // Color
        !isSelected && colorScheme === 'dark'
          ? [
              'bg-transparent',
              'hactive:bg-light-ui-100/10',
              'focus-visible:ring-3',
              'focus-visible:ring-canary-500',
              'focus-visible:bg-modal-overlay-canary/20',
            ]
          : '',
        !isSelected && colorScheme === 'light'
          ? [
              'bg-transparent',
              'hactive:bg-dark-ui-100/10',
              'focus-visible:ring-3',
              'focus-visible:ring-grape-500',
              'focus-visible:bg-modal-overlay-grape/20',
            ]
          : '',
        isSelected && colorScheme === 'dark'
          ? ['bg-light-ui-100', 'focus-visible:ring-3', 'focus-visible:ring-canary-500']
          : '',
        isSelected && colorScheme === 'light'
          ? ['bg-dark-ui-100', 'focus-visible:ring-3', 'focus-visible:ring-grape-500']
          : '',

        // Transition
        'transition-all',
        'duration-150',
        'ease-in-out',

        // Children positioning
        'flex',
        'items-center',
        'justify-center',
        'relative',

        // Children styling
        'text-body-small',
        {
          'text-dark-text-100': colorScheme === 'dark' && isSelected,
          'text-light-text-100': colorScheme === 'light' && isSelected,
          'text-light-text-200 hhover:text-light-text-100 hactive:text-light-text-100':
            colorScheme === 'dark' && !props.disabled && !isSelected,
          'text-dark-text-200 hhover:text-dark-text-100 hactive:text-dark-text-100':
            colorScheme === 'light' && !props.disabled && !isSelected,
          'text-light-text-400': colorScheme === 'dark' && props.disabled,
          'text-dark-text-400': colorScheme === 'light' && props.disabled,
        },
      ]"
      :disabled="disabled"
      @pointerenter="onPointerEnter"
      @focus="!disabled && onPointerEnter"
      @pointerleave="onPointerLeave"
      @blur="!disabled && onPointerLeave"
      @pointerup="onPointerUp"
      @click="onPointerDown"
    >
      <span v-if="iconName === 'attachment'" :class="['text-body-extra-small', 'font-semibold', 'font-sans']">
        +{{ numberOfAttachmentTypesAllowed }}</span
      >
      <CursorDottedShapeIcon
        v-else-if="isExpandedSelectTool"
        :dark-mode="isSelected ? colorScheme === 'light' : colorScheme === 'dark'"
        :is-selected="isSelected"
      />
      <OzIcon
        v-else-if="!isSvgIcon || isToolAPadletTool"
        :font="isToolAPadletTool ? 'oricon' : 'wbicons'"
        :name="iconName"
        :size="isTouch ? 26 : 24"
      ></OzIcon>
      <WhiteboardSvgIcon
        v-else
        :name="iconName"
        :class="[
          iconColorClass,
          {
            'ms-0.5': !!secondaryMenu,
          },
        ]"
      />
    </OzBaseButton>

    <!-- Corner indicator -->
    <div
      :class="[
        // Positioning
        'absolute',
        'end-0',
        'bottom-0',
        'z-sidepanel',
        // Shape
        'w-0',
        'h-0',

        // Transition
        'transition-all',
        'duration-150',
        'ease-in-out',
        'pointer-events-none',
      ]"
      :style="{
        borderColor: 'transparent',
        borderBottomColor: currentToolPreviewColorHex,
        borderStyle: 'solid',
        borderWidth: 0,
        borderInlineStartWidth: isHovered && !isSelected ? '12px' : isSelected ? '16px' : 0,
        borderBlockEndWidth: isHovered && !isSelected ? '12px' : isSelected ? '16px' : 0,
        borderRadius: 0,
        borderEndEndRadius: '6px',
      }"
    ></div>
    <!-- Corner diagonal line -->
    <svg
      v-if="isSelected && cornerIndicatorNeedsDiagonalLine"
      :class="[
        'absolute',
        'end-0',
        'bottom-0',
        'z-sidepanel',
        {
          'text-dark-text-300': (colorScheme === 'dark' && isSelected) || (colorScheme === 'light' && !isSelected),
          'text-light-text-300': (colorScheme === 'light' && isSelected) || (colorScheme === 'dark' && !isSelected),
        },
        {
          '-scale-x-100': isRtl,
        },
      ]"
      :style="{
        height: isHovered && !isSelected ? '12px' : '16px',
        width: isHovered && !isSelected ? '12px' : '16px',
      }"
      :viewBox="isHovered && !isSelected ? '0 0 12 12' : '0 0 16 16'"
      aria-hidden="true"
    >
      <path
        :d="isHovered && !isSelected ? 'M 12 0 L 12 2 L 2 12 L 0 12 Z' : 'M 16 0 L 16 2 L 2 16 L 0 16 Z'"
        fill="currentColor"
      />
    </svg>

    <!-- Tooltip -->
    <Portal selector="#surface-container">
      <Transition
        enter-active-class="transition-opacity duration-250 ease-out motion-reduce:transition-none"
        leave-active-class="transition-opacity duration-150 ease-in motion-reduce:transition-none"
        enter-class="opacity-0"
        leave-to-class="opacity-0"
        appear
      >
        <OzBasePopover
          v-if="!isSelected && xTooltip && buttonEl"
          :class="[
            'px-3 py-1',
            'pointer-events-none',
            {
              'bg-common-ui-d70 ': colorScheme === 'dark' && colorStyle === 'frosted',
              'bg-common-ui-l86': colorScheme === 'light' && colorStyle === 'frosted',
              'bg-dark-ui-100 ': colorScheme === 'dark' && colorStyle === 'opaque',
              'bg-light-ui-100': colorScheme === 'light' && colorStyle === 'opaque',
            },
            'backdrop-blur-6',
            'z-modal',
          ]"
          :config="{
            placement: whiteboardStore.isLoadingPortraitMobileLayout ? 'top' : isRtl ? 'right' : 'left',
            middleware: [offset(whiteboardToolbarStore.tooltipOffset)],
          }"
          role="tooltip"
          :anchor="buttonEl"
          :radius="8"
          :color="OzBasePopoverColors.None"
        >
          <span :class="colorScheme === 'dark' ? 'text-light-text-100' : 'text-dark-text-100'">{{ label }}</span
          ><template v-if="shortcut"
            >&nbsp;<span :class="['ms-1.5', colorScheme === 'dark' ? 'text-light-text-300' : 'text-dark-text-300']"
              >{{ shortcut }}
            </span></template
          ></OzBasePopover
        >
      </Transition>
    </Portal>
  </div>
</template>
